/** MapMark bootstrap: state, fonts, routing, sync, service worker. */
import './styles/app.css';
import { setUnauthorizedHandler } from './app/api.ts';
import { refreshMapList } from './app/maps.ts';
import { navigate, onRoute, parseRoute, type Route } from './app/router.ts';
import { app, loadPersistedState, setSession } from './app/state.ts';
import { loadClock } from './app/clock.ts';
import { adoptDevice, requestSync, startSyncTriggers } from './app/sync.ts';
import { loadFonts } from './render/fonts.ts';
import { renderAdmin } from './ui/admin.ts';
import { trackGlass } from './ui/glass.ts';
import { renderEditor } from './ui/editor-screen.ts';
import { renderLibrary } from './ui/library.ts';
import { renderSignIn } from './ui/signin.ts';
import { h, toast } from './ui/dom.ts';
import * as zoom from './ui/zoom.ts';
import { lockViewport } from './ui/lock.ts';
import { installFieldPad } from './ui/fieldpad.ts';

// One public origin: a device's maps live in that origin's storage, so www and workers.dev hand
// over to it. (Done here, not in the Worker: pages are static assets and never invoke it.)
const CANONICAL_HOST = 'mapmark.app';
if (import.meta.env.PROD && location.hostname !== CANONICAL_HOST && (location.hostname === `www.${CANONICAL_HOST}` || location.hostname.endsWith('.workers.dev'))) {
  location.replace(`https://${CANONICAL_HOST}${location.pathname}${location.search}${location.hash}`);
}

const root = document.getElementById('app')!;
let teardown: (() => void) | null = null;

// Safari on iPhone and iPad shows :active (the pressed state of every button) only on pages that listen for touches
document.addEventListener('touchstart', () => {}, { passive: true });
// the app's own screen never zooms or pans (only the plan does, in the editor)
lockViewport();
// the number pad for every field where fingers type (ui/fieldpad.ts)
installFieldPad();

/*
 * A new screen fades in (app.css) while its own pieces move into place. Not a view transition:
 * WebKit takes no pointer input while one runs, and a finger put on the plan as a map opens
 * must never be lost.
 */
function show(route: Route): void {
  // A card tapped, or Back in a map, has prepared a zoom (ui/zoom.ts). Opening, the library stays
  // where it is — under the editor, which mounts unseen — until the hero has landed; going back,
  // the library mounts under the hero at once.
  zoom.finishNow();
  const hero = zoom.take();
  const opening = hero?.kind === 'open' && route.name === 'editor' && route.id === hero.id && app.state.session ? hero : null;
  const returning = hero?.kind === 'back' && route.name === 'library' && app.state.session ? hero : null;
  if (hero && !opening && !returning) zoom.drop(hero);
  const before = teardown;
  const oldScreen = root.lastElementChild as HTMLElement | null;
  teardown = null;
  if (!opening) before?.();
  mount(route);
  const screen = root.lastElementChild as HTMLElement | null;
  if (opening && screen && screen !== oldScreen) zoom.open(opening, root, oldScreen, screen, () => before?.());
  else if (opening) before?.();
  if (returning && screen) zoom.back(returning, root, screen);
}

function mount(route: Route): void {
  const s = app.state;
  if (route.name === 'admin') {
    teardown = renderAdmin(root);
    return;
  }
  if (!s.session) {
    teardown = renderSignIn(root, () => {
      void refreshMapList();
      requestSync('now');
      navigate({ name: 'library' }, true);
    });
    return;
  }
  if (route.name === 'signin') {
    navigate({ name: 'library' }, true);
    return;
  }
  if (route.name === 'editor') {
    teardown = renderEditor(root, route.id);
    return;
  }
  teardown = renderLibrary(root);
}

async function boot(): Promise<void> {
  setUnauthorizedHandler(() => {
    void setSession(null).then(() => {
      toast('Please sign in again.', 'error');
      navigate({ name: 'signin' }, true);
    });
  });
  await Promise.all([loadPersistedState(), loadClock()]);
  // (a device signed in before it kept track of whose maps it holds: they are this user's)
  if (app.state.session) await adoptDevice(app.state.session.user.id);
  await refreshMapList();
  app.set({ ready: true });
  onRoute(show);
  show(parseRoute());
  void loadFonts();
  startSyncTriggers();
  requestSync('foreground');
  registerServiceWorker();
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          nw?.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              // a notice of its own, kept until it is answered: never the messages' pill, which the next hint would take over
              if (document.getElementById('update')) return;
              const bar = h('div', { id: 'update', class: 'glass', role: 'status' }, 'MapMark was updated', h('button', { class: 'btn', type: 'button', onClick: () => location.reload() }, 'Reload'));
              document.body.append(bar);
              trackGlass(bar, { gpu: false });
              void bar.offsetWidth;
              bar.classList.add('show');
            }
          });
        });
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void reg.update();
        });
      })
      .catch((e) => console.warn('sw registration failed', e));
  });
}

void boot();

// (dev: a scenario run in the page itself, named in the address — `#run=/scripts/bar/scenario.js&…` — so the
// same steps can be taken, and what is painted read, in Safari on a Mac or an iPad as in WebKit: scripts/bar)
if (import.meta.env.DEV) {
  const run = new URLSearchParams(location.hash.slice(1)).get('run');
  if (run?.startsWith('/scripts/')) void import(/* @vite-ignore */ run);
}
